import { MethodsOfUseForm } from "../../types/types";
import { useCallback, useContext, useMemo, useRef } from "react";
import { useWatch } from "react-hook-form";
import { useStoreState } from "../../util/util";
import { endpoints } from "../../endpoints";
import { Auth } from "../Auth";
import styled from "styled-components";
import { RadioButton } from "../RadioButton/RadioButton";
import { useTranslation } from "react-i18next";
import { H4, H6Bold, SmallText } from "../Typography/Typography";
import { MultiSelectField } from "./MultiSelectField";
import { AccessibilityData } from "../../types/types.PIM";
import { ACCESSIBILITY_TYPES } from "../../pages/SharedPages/ProductDetailPage/Assets/util";

type AccessibilityType =
  typeof ACCESSIBILITY_TYPES[keyof typeof ACCESSIBILITY_TYPES];

interface MultiSelectConfig {
  id: string;
  displayName: string;
  baseUrl: string;
  existingDataKey: string;
  showForAccessibility: AccessibilityType[];
}

const AccessibilityContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const AccordionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 20px;
  border: 1px solid ${({ theme }) => theme.resolve("color_border_lighter")};
  border-radius: 8px;
  width: 100%;
  box-sizing: border-box;
`;

const MarginBottomH6 = styled(H6Bold)`
  margin-bottom: 8px;
`;

const SectionContainer = styled.div<{ marginBottom?: string }>`
  margin-bottom: ${(props) => props.marginBottom || "0"};
`;

const createMultiSelectConfigs = (
  storefront_id: string,
  tenant_id: string
): MultiSelectConfig[] => [
  {
    id: "input_customer_ids",
    displayName: "Customer/Distributor Name",
    baseUrl: endpoints.v2_storefronts_id_tenants_id_tenants_basic(
      storefront_id,
      tenant_id
    ),
    existingDataKey: "customers",
    showForAccessibility: [ACCESSIBILITY_TYPES.LOGGED_IN_USER],
  },
  {
    id: "input_role_ids",
    displayName: "Roles",
    baseUrl: `/v1/storefronts/${storefront_id}/roles`,
    existingDataKey: "roles",
    showForAccessibility: [
      ACCESSIBILITY_TYPES.LOGGED_IN_USER,
      ACCESSIBILITY_TYPES.INTERNAL,
    ],
  },
  {
    id: "input_user_ids",
    displayName: "User Name",
    baseUrl: endpoints.v1_storefronts_id_tenants_id_users(
      storefront_id,
      tenant_id
    ),
    existingDataKey: "users",
    showForAccessibility: [
      ACCESSIBILITY_TYPES.LOGGED_IN_USER,
      ACCESSIBILITY_TYPES.INTERNAL,
    ],
  },
];

const getAccessibilityOptions = (t: any) => [
  {
    value: ACCESSIBILITY_TYPES.PUBLIC,
    title: t("Public"),
  },
  {
    value: ACCESSIBILITY_TYPES.LOGGED_IN_USER,
    title: t("Customers & Internal Users"),
  },
  {
    value: ACCESSIBILITY_TYPES.INTERNAL,
    title: t("Internal Users"),
  },
];

interface DocumentAccessibilityProps {
  accessibility: AccessibilityType;
  methodsOfUseForm: MethodsOfUseForm;
  onAccessibilityChange?: (newAccessibility: string) => void;
  existingData: { [key: string]: AccessibilityData[] };
}

const AccessibilityRadioOptions = ({
  accessibility,
  onAccessibilityChange,
  t,
}: {
  accessibility: string;
  onAccessibilityChange: (e: React.FormEvent<HTMLInputElement>) => void;
  t: any;
}) => {
  return (
    <>
      {getAccessibilityOptions(t).map((option) => (
        <div key={option.value} style={{ width: "fit-content" }}>
          <RadioButton
            key={option.value}
            name="accessibility"
            value={option.value}
            checked={accessibility === option.value}
            optionTitle={option.title}
            handleChange={onAccessibilityChange}
          />
        </div>
      ))}
    </>
  );
};

export const DocumentAccessibility = ({
  accessibility,
  methodsOfUseForm,
  onAccessibilityChange,
  existingData,
}: DocumentAccessibilityProps) => {
  const { t } = useTranslation();
  const { storefront_id } = useStoreState();
  const { user } = useContext(Auth);

  const multiSelectRefs = useRef<{ [key: string]: { clearChips: () => void } }>(
    {}
  );

  const handleAccessibilityChange = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      if (onAccessibilityChange && e.currentTarget.value) {
        onAccessibilityChange(e.currentTarget.value);
      }
    },
    [onAccessibilityChange]
  );

  const watchedFields = {
    role:
      (useWatch({
        control: methodsOfUseForm.control,
        name: "input_role_ids",
      }) as string[]) || [],
    user:
      (useWatch({
        control: methodsOfUseForm.control,
        name: "input_user_ids",
      }) as string[]) || [],
    customer:
      (useWatch({
        control: methodsOfUseForm.control,
        name: "input_customer_ids",
      }) as string[]) || [],
  };

  const multiSelectConfigs = useMemo(
    () => createMultiSelectConfigs(storefront_id, user?.tenant_id || ""),
    [storefront_id, user?.tenant_id]
  );

  const visibleConfigs = multiSelectConfigs.filter((config) =>
    config.showForAccessibility.includes(accessibility as AccessibilityType)
  );

  const customersConfig = visibleConfigs.find(
    (config) => config.id === "input_customer_ids"
  );
  const internalConfigs = visibleConfigs.filter(
    (config) => config.id === "input_role_ids" || config.id === "input_user_ids"
  );

  return (
    <AccessibilityContainer>
      <H4>{t("Accessibility")}</H4>
      <SmallText>{t("Document can be downloaded by:")}</SmallText>

      <AccessibilityRadioOptions
        accessibility={accessibility}
        onAccessibilityChange={handleAccessibilityChange}
        t={t}
      />

      {accessibility !== ACCESSIBILITY_TYPES.PUBLIC && (
        <AccordionContainer>
          {accessibility === ACCESSIBILITY_TYPES.LOGGED_IN_USER &&
            customersConfig && (
              <SectionContainer marginBottom={"12px"}>
                {
                  <MarginBottomH6>
                    {t("Customers & Distributors")}
                  </MarginBottomH6>
                }
                <MultiSelectField
                  id={customersConfig.id}
                  key={customersConfig.id}
                  fieldValues={watchedFields.customer}
                  display_name={customersConfig.displayName}
                  methodsOfUseForm={methodsOfUseForm}
                  multiSelectRefs={multiSelectRefs}
                  baseUrl={customersConfig.baseUrl}
                  existingData={
                    existingData[customersConfig.existingDataKey] || []
                  }
                  userAndRolesValues={undefined}
                />
              </SectionContainer>
            )}

          <SectionContainer>
            <MarginBottomH6>{t("Internal Users")}</MarginBottomH6>
            {internalConfigs.map((config) => {
              const userAndRolesValues =
                config.id === "input_role_ids"
                  ? watchedFields.user
                  : config.id === "input_user_ids"
                  ? watchedFields.role && watchedFields.role.length > 0
                    ? watchedFields.role
                    : (existingData.roles || []).map((r) => r.id)
                  : undefined;

              return (
                <MultiSelectField
                  key={config.id}
                  id={config.id}
                  fieldValues={
                    watchedFields[
                      config.existingDataKey as keyof typeof watchedFields
                    ]
                  }
                  display_name={config.displayName}
                  methodsOfUseForm={methodsOfUseForm}
                  multiSelectRefs={multiSelectRefs}
                  baseUrl={config.baseUrl}
                  existingData={existingData[config.existingDataKey] || []}
                  userAndRolesValues={userAndRolesValues}
                />
              );
            })}
          </SectionContainer>
        </AccordionContainer>
      )}
    </AccessibilityContainer>
  );
};
